import { useEffect, useRef, useState } from "react";
import { academyRequest, createCommandSender } from "./academyApi";
import { checkIdentitySession } from "./identityClient";
import { useIdentity } from "./useIdentity";
import { ACADEMIC_CATALOGUE_CHANGED, useAcademicRead } from "./useAcademicCatalogue";
import { academicInstant } from "./academicTime";
import type {
  AcademicCohortPage,
  AcademicCohortSummary,
  AcademicCohortDefinition,
  AcademicCohortDefinitionVersion,
  CohortProgramOptionPage,
  CohortProgramOption,
} from "./generated/academic-catalogue";

const transitions: Record<string, string[]> = {
  planning: ["open", "cancel"],
  open: ["pause", "close", "cancel"],
  limited: ["pause", "close", "cancel"],
  full: ["pause", "close", "cancel"],
  paused: ["open", "close", "cancel"],
  closed: ["complete", "cancel"],
  completed: [],
  cancelled: [],
};
const localTime = (value: string, zone: string) => {
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat("en-CA", {
      timeZone: zone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    })
      .formatToParts(new Date(value))
      .map(part => [part.type, part.value]),
  );
  return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}`;
};
export function AcademicCohortFields(props: {
  kind: "organization" | "institution";
  record?: Record<string, unknown>;
  action?: string;
  formId: string;
  onSaved: () => void;
  onBusy: (busy: boolean) => void;
}) {
  const account = useIdentity();
  const session = account.session;
  if (!session) return <p role="alert">Load your current Academy workspace before editing a cohort.</p>;
  const context = `${session.tenantId}:${session.user.id}:${session.workspace.id}`;
  return (
    <CohortFields
      key={`${context}:${props.record?.id || "new"}`}
      {...props}
      context={context}
      canManage={session.permissions.includes("catalogue.cohort.manage")}
    />
  );
}
function CohortFields({
  kind,
  record,
  action: initialAction,
  formId,
  onSaved,
  onBusy,
  context,
  canManage,
}: Parameters<typeof AcademicCohortFields>[0] & { context: string; canManage: boolean }) {
  const [basis, setBasis] = useState<AcademicCohortSummary>(),
    [programId, setProgramId] = useState("");
  const [name, setName] = useState(""),
    [timezone, setTimezone] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC"),
    [language, setLanguage] = useState("");
  const [start, setStart] = useState(""),
    [end, setEnd] = useState(""),
    [deadline, setDeadline] = useState("");
  const [capacity, setCapacity] = useState(""),
    [delivery, setDelivery] = useState<AcademicCohortDefinition["deliveryMode"]>("online"),
    [privateCohort, setPrivate] = useState(true),
    [track, setTrack] = useState("");
  const [scheduleVersionId,setScheduleVersionId]=useState(""),
    [eligibilityPolicyVersionId,setEligibilityPolicyVersionId]=useState(""),
    [capacityPolicyVersionId,setCapacityPolicyVersionId]=useState("");
  const [sessions, setSessions] = useState([{ startsAt: "", endsAt: "" }]);
  const [action, setAction] = useState(initialAction || "update"),
    [reason, setReason] = useState(""),
    [error, setError] = useState(""),
    [busy, setBusy] = useState(false);
  const sender = useRef(createCommandSender({ headerOnly: true })),
    submitting = useRef(false);
  const source = useAcademicRead(`cohort-dialog:${record?.id || "new"}`, true, async signal => {
    if (record) {
      let after: string | null = null;
      const seen = new Set<string>();
      do {
        const page: AcademicCohortPage = await academyRequest(
          `academic-catalogue/cohorts${after ? `?after=${encodeURIComponent(after)}` : ""}`,
          undefined,
          "GET",
          true,
          undefined,
          signal,
        );
        const cohort = page.items.find(item => item.id === record.id);
        if (cohort) return { cohort, options: [] as CohortProgramOption[], definitions: [] as AcademicCohortDefinitionVersion[] };
        after = page.nextCursor;
        if (after && seen.has(after)) throw new Error("Cohort pagination did not advance.");
        if (after) seen.add(after);
      } while (after);
      throw new Error("The selected cohort is no longer available in this workspace.");
    }
    const options: CohortProgramOption[] = [];
    let after: string | null = null;
    const seen = new Set<string>();
    do {
      const page: CohortProgramOptionPage = await academyRequest(
        `academic-catalogue/cohorts/program-options${after ? `?after=${encodeURIComponent(after)}` : ""}`,
        undefined,
        "GET",
        true,
        undefined,
        signal,
      );
      options.push(...page.items);
      after = page.nextCursor;
      if (after && seen.has(after)) throw new Error("Program options did not advance.");
      if (after) seen.add(after);
    } while (after);
    const definitions=await academyRequest<AcademicCohortDefinitionVersion[]>(
      "academic-catalogue/cohort-definitions",undefined,"GET",true,undefined,signal);
    return { cohort: undefined, options, definitions:definitions.filter(item=>item.versionStatus==="published"&&item.versionId) };
  });
  useEffect(() => {
    const row = source.data?.cohort;
    if (!row) return;
    setBasis(row);
    setName(row.name);
    setProgramId(row.programId);
    setTimezone(row.timezone);
    setLanguage(row.language);
    setStart(localTime(row.startAt, row.timezone));
    setEnd(localTime(row.endAt, row.timezone));
    setDeadline(localTime(row.applicationDeadlineAt, row.timezone));
    setCapacity(String(row.capacity));
    setDelivery(row.deliveryMode);
    setPrivate(row.privateCohort);
    setTrack(row.ageTrackId || "");
    setSessions(
      row.schedule?.sessions.map(item => ({
        startsAt: localTime(item.startsAt, row.timezone),
        endsAt: localTime(item.endsAt, row.timezone),
      })) || [{ startsAt: "", endsAt: "" }],
    );
  }, [source.data]);
  const selected = source.data?.options.find(item => item.id === programId),
    transition = !!record && action !== "update";
  const available = transitions[basis?.status || ""] || [];
  const editable = !basis || ["planning", "paused"].includes(basis.status);
  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (submitting.current || !canManage || source.loading || source.error) return;
    submitting.current = true;
    setBusy(true);
    onBusy(true);
    setError("");
    try {
      const actual = await checkIdentitySession();
      if (`${actual.tenantId}:${actual.user.id}:${actual.workspace.id}` !== context)
        throw new Error("Your workspace changed. Reload before saving this cohort.");
      if (transition) {
        if (!basis || !available.includes(action)) throw new Error("Select an available cohort command.");
        await sender.current(`academic-catalogue/cohorts/${basis.id}/commands/${action}`, {
          expectedVersion: basis.version,
          reason,
        });
      } else {
        if (!editable) throw new Error("Pause this cohort before editing its dates and schedule.");
        const definition = {
          name,
          timezone,
          language,
          deliveryMode: delivery,
          capacity: Number(capacity),
          privateCohort: kind === "organization" || privateCohort,
          startAt: academicInstant(start, timezone),
          endAt: academicInstant(end, timezone),
          applicationDeadlineAt: academicInstant(deadline, timezone),
          ...(track ? { ageTrackId: track } : {}),
          schedule: basis
            ? basis.schedule
            : scheduleVersionId
            ? source.data?.definitions.find(item=>item.versionId===scheduleVersionId)?.payload as AcademicCohortDefinition["schedule"]
            : {
                sessions: sessions.map(item => ({
                  startsAt: academicInstant(item.startsAt, timezone),
                  endsAt: academicInstant(item.endsAt, timezone),
                })),
              },
        };
        if (basis)
          await sender.current(`academic-catalogue/cohorts/${basis.id}/commands/update`, {
            expectedVersion: basis.version,
            definition,
            reason,
          });
        else {
          if (!selected) throw new Error("Choose a currently available published Program.");
          await sender.current("academic-catalogue/cohorts/commands/create", {
            programId: selected.id,
            programVersionId: selected.programVersionId,
            curriculumVersionId: selected.curriculumVersionId,
            scheduleVersionId,
            eligibilityPolicyVersionId,
            capacityPolicyVersionId,
            sections: [],
            definition,
            reason,
          });
        }
      }
      window.dispatchEvent(new CustomEvent(ACADEMIC_CATALOGUE_CHANGED));
      onSaved();
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : "The cohort could not be saved.");
    } finally {
      submitting.current = false;
      setBusy(false);
      onBusy(false);
    }
  };
  const zones = Array.from(
    new Set([
      timezone,
      "UTC",
      ...((Intl as typeof Intl & { supportedValuesOf?: (key: string) => string[] }).supportedValuesOf?.(
        "timeZone",
      ) || []),
    ]),
  );
  return (
    <form id={formId} onSubmit={submit}>
      {source.loading && <p role="status">Loading current cohort authority…</p>}
      {(source.error || error) && <p role="alert">{source.error || error}</p>}
      {!canManage && <p role="alert">Your current workspace has no cohort management permission.</p>}
      {basis && (
        <label className="field">
          Action
          <select
            aria-label="Action"
            className="select"
            value={action}
            onChange={event => setAction(event.target.value)}
            disabled={busy}
          >
            <option value="update" disabled={!editable}>
              Edit cohort details
            </option>
            {available.map(item => (
              <option key={item} value={item}>
                {item[0].toUpperCase() + item.slice(1)} cohort
              </option>
            ))}
          </select>
        </label>
      )}
      <fieldset
        disabled={busy || source.loading || !!source.error || transition || !editable || !canManage}
        style={{ border: 0, padding: 0, margin: 0 }}
      >
        <div className="form-grid">
          <label className="field">
            Cohort name
            <input
              aria-label="Cohort name"
              className="input"
              required
              minLength={3}
              maxLength={240}
              value={name}
              onChange={e => setName(e.target.value)}
            />
          </label>
          <label className="field">
            Program
            <select
              aria-label="Program"
              className="select"
              required
              value={programId}
              disabled={!!basis}
              onChange={e => {
                setProgramId(e.target.value);
                setTrack("");
              }}
            >
              <option value="">Choose a published Program</option>
              {basis ? (
                <option value={basis.programId}>
                  {basis.programName || "Unresolved academic definition"}
                </option>
              ) : (
                source.data?.options.map(item => (
                  <option key={item.id} value={item.id}>
                    {item.name} · Version {item.programVersionNumber}
                  </option>
                ))
              )}
            </select>
          </label>
          <label className="field">
            Exact Program version
            <input
              className="input"
              readOnly
              value={basis?.programVersionId || selected?.programVersionId || ""}
            />
          </label>
          <label className="field">
            Exact curriculum version
            <input
              className="input"
              readOnly
              value={basis?.curriculumVersionId || selected?.curriculumVersionId || ""}
            />
          </label>
          {!basis && <>
            <label className="field">
              Published schedule
              <select aria-label="Published schedule" className="select" required value={scheduleVersionId}
                onChange={event=>setScheduleVersionId(event.target.value)}>
                <option value="">Choose a reusable schedule</option>
                {source.data?.definitions.filter(item=>item.kind==="schedule").map(item=><option key={item.versionId!} value={item.versionId!}>{item.title} · v{item.versionNo}</option>)}
              </select>
            </label>
            <label className="field">
              Published eligibility policy
              <select aria-label="Published eligibility policy" className="select" required value={eligibilityPolicyVersionId}
                onChange={event=>setEligibilityPolicyVersionId(event.target.value)}>
                <option value="">Choose an eligibility policy</option>
                {source.data?.definitions.filter(item=>item.kind==="eligibility").map(item=><option key={item.versionId!} value={item.versionId!}>{item.title} · v{item.versionNo}</option>)}
              </select>
            </label>
            <label className="field">
              Published capacity policy
              <select aria-label="Published capacity policy" className="select" required value={capacityPolicyVersionId}
                onChange={event=>{setCapacityPolicyVersionId(event.target.value);const definition=source.data?.definitions.find(item=>item.versionId===event.target.value);const hard=definition?.payload?.hardCapacity;if(typeof hard==="number")setCapacity(String(hard));}}>
                <option value="">Choose a capacity policy</option>
                {source.data?.definitions.filter(item=>item.kind==="capacity").map(item=><option key={item.versionId!} value={item.versionId!}>{item.title} · v{item.versionNo}</option>)}
              </select>
            </label>
          </>}
          <label className="field">
            Time zone
            <select
              aria-label="Time zone"
              className="select"
              required
              value={timezone}
              onChange={e => setTimezone(e.target.value)}
            >
              {zones.map(zone => (
                <option key={zone}>{zone}</option>
              ))}
            </select>
          </label>
          <label className="field">
            Language
            <input
              className="input"
              required
              maxLength={120}
              value={language}
              onChange={e => setLanguage(e.target.value)}
            />
          </label>
          <label className="field">
            Starts
            <input
              className="input"
              type="datetime-local"
              required
              value={start}
              onChange={e => setStart(e.target.value)}
            />
          </label>
          <label className="field">
            Ends
            <input
              className="input"
              type="datetime-local"
              required
              value={end}
              onChange={e => setEnd(e.target.value)}
            />
          </label>
          <label className="field">
            Application deadline
            <input
              className="input"
              type="datetime-local"
              required
              value={deadline}
              onChange={e => setDeadline(e.target.value)}
            />
          </label>
          <label className="field">
            Capacity
            <input
              aria-label="Capacity"
              className="input"
              type="number"
              required
              min={1}
              max={100000}
              readOnly={!!basis||!!capacityPolicyVersionId}
              value={capacity}
              onChange={e => setCapacity(e.target.value)}
            />
            {(basis||capacityPolicyVersionId) && <small>Capacity is pinned by the published capacity-policy version.</small>}
          </label>
          <label className="field">
            Delivery mode
            <select
              aria-label="Delivery mode"
              className="select"
              value={delivery}
              onChange={e => setDelivery(e.target.value as typeof delivery)}
            >
              <option value="online">Online</option>
              <option value="physical">Physical</option>
              <option value="hybrid">Hybrid</option>
            </select>
          </label>
          <label className="field">
            Age track
            <select
              aria-label="Age track"
              className="select"
              value={track}
              disabled={!!basis}
              onChange={e => setTrack(e.target.value)}
            >
              <option value="">No age track</option>
              {basis?.ageTrackId ? (
                <option value={basis.ageTrackId}>{basis.ageTrackName}</option>
              ) : (
                selected?.ageTracks.map(item => (
                  <option key={item.id} value={item.id}>
                    {item.name}
                  </option>
                ))
              )}
            </select>
          </label>
          <label className="field">
            <input
              type="checkbox"
              checked={privateCohort || kind === "organization"}
              disabled={kind === "organization"}
              onChange={e => setPrivate(e.target.checked)}
            />{" "}
            Private cohort
          </label>
        </div>
        {basis ? (
          <p role="status">The exact published schedule pinned to this cohort is preserved when you edit other details.</p>
        ) : scheduleVersionId ? (
          <p role="status">The selected published schedule supplies the exact reusable session or study-day plan.</p>
        ) : (
          sessions.map((item, index) => (
            <div className="form-grid" key={index}>
              <label className="field">
                Session {index + 1} starts
                <input
                  className="input"
                  type="datetime-local"
                  required
                  value={item.startsAt}
                  onChange={e =>
                    setSessions(values =>
                      values.map((v, i) => (i === index ? { ...v, startsAt: e.target.value } : v)),
                    )
                  }
                />
              </label>
              <label className="field">
                Session {index + 1} ends
                <input
                  className="input"
                  type="datetime-local"
                  required
                  value={item.endsAt}
                  onChange={e =>
                    setSessions(values =>
                      values.map((v, i) => (i === index ? { ...v, endsAt: e.target.value } : v)),
                    )
                  }
                />
              </label>
              {sessions.length > 1 && (
                <button
                  type="button"
                  className="btn btn-sm"
                  onClick={() => setSessions(values => values.filter((_, i) => i !== index))}
                >
                  Remove session {index + 1}
                </button>
              )}
            </div>
          ))
        )}
        {!basis&&!scheduleVersionId && (
          <button
            type="button"
            className="btn btn-sm"
            disabled={sessions.length >= 100}
            onClick={() => setSessions(values => [...values, { startsAt: "", endsAt: "" }])}
          >
            Add session
          </button>
        )}
      </fieldset>
      {!record && !source.loading && !source.error && !source.data?.options.length && (
        <p>No eligible published Program versions are available for this workspace.</p>
      )}
      <label className="field">
        Reason
        <textarea
          className="textarea"
          required
          minLength={10}
          maxLength={2000}
          value={reason}
          onChange={e => setReason(e.target.value)}
          disabled={busy}
        />
      </label>
    </form>
  );
}
